import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import {
  Quote,
  selectClient,
  selectIllustration,
  selectHasActiveIllustrationRequest,
  selectSessionAdvisor,
  markIllustrationAsDownloaded,
  postIllustrationRequestRequest,
} from '@client/model';
import { downloadFile } from '@client/util/secureDownload';

type SupportedQuote = Quote | TraditionalLTCQuote;

interface UseIllustrationDownloadFlowProps {
  quote: SupportedQuote;
  selectedPremium?: number;
  clientPartner?: Client | null;
  fundingSourceId?: string;
}

export function useIllustrationDownloadFlow({
  quote,
  selectedPremium,
  clientPartner,
  fundingSourceId,
}: UseIllustrationDownloadFlowProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const advisor = useSelector(selectSessionAdvisor);
  const illustration = useSelector(selectIllustration);
  const hasActiveIllustrationRequest = useSelector(selectHasActiveIllustrationRequest);
  
  // Track if user initiated a download to trigger auto-download when PDF becomes available
  const userInitiatedDownload = useRef(false);
  const hasAutoDownloaded = useRef(false);

  const isTraditionalLTC = 'traditionalLTCQuoteId' in quote;
  const quoteId = isTraditionalLTC ? quote.traditionalLTCQuoteId : quote.insuranceQuoteId;
  
  const activeRequest = illustration.activeRequests?.[quoteId];
  const completedIllustration = illustration.completedIllustrations?.[quoteId];

  const currentPremium = isTraditionalLTC 
    ? quote.primaryModalPremium 
    : (selectedPremium || quote.premiumAmount);

  const isIllustrationForCurrentPremium = (illustrationPremium?: number) => {
    if (isTraditionalLTC) {
      return true;
    }
    if (!illustrationPremium) {
      return currentPremium === quote.premiumAmount;
    }
    return illustrationPremium === currentPremium;
  };

  // Check if we have a valid illustration for the current premium
  const hasValidCompletedIllustration =
    completedIllustration?.pdfUrl &&
    isIllustrationForCurrentPremium(completedIllustration.premiumAmount);

  const hasValidActiveIllustration =
    activeRequest?.pdfUrl && isIllustrationForCurrentPremium(activeRequest.premiumAmount);

  const hasIllustrationPdf = hasValidCompletedIllustration || hasValidActiveIllustration;

  const isActiveStatus =
    activeRequest?.status === 'initiated' || activeRequest?.status === 'polling';
  const isCreatingIllustration =
    isActiveStatus && isIllustrationForCurrentPremium(activeRequest.premiumAmount);

  const isThisQuoteBeingCreated = isCreatingIllustration;
  const isAnotherQuoteBeingCreated = hasActiveIllustrationRequest && !isThisQuoteBeingCreated;

  const handleViewPdf = (event: React.MouseEvent) => {
    event.stopPropagation();

    // Get the PDF for the current premium amount
    let pdfUrl: string | null = null;
    let requestId: string | null = null;

    if (hasValidCompletedIllustration && completedIllustration) {
      pdfUrl = completedIllustration.pdfUrl;
      requestId = completedIllustration.requestId;
    } else if (hasValidActiveIllustration && activeRequest) {
      pdfUrl = activeRequest.pdfUrl;
      requestId = activeRequest.requestId;
    }

    if (pdfUrl && requestId) {
      window.open(pdfUrl, '_blank');
      dispatch(markIllustrationAsDownloaded({ requestId }));
    }
  };

  const handleDownloadPdf = async (event: React.MouseEvent) => {
    event.stopPropagation();

    let pdfUrl: string | null = null;
    let requestId: string | null = null;

    if (hasValidCompletedIllustration && completedIllustration) {
      pdfUrl = completedIllustration.pdfUrl;
      requestId = completedIllustration.requestId;
    } else if (hasValidActiveIllustration && activeRequest) {
      pdfUrl = activeRequest.pdfUrl;
      requestId = activeRequest.requestId;
    }

    if (pdfUrl && requestId) {
      
      const filename = `policy-illustration-${quoteId}.pdf`;
      const result = await downloadFile(pdfUrl, filename);
      
      if (result.success) {
        dispatch(markIllustrationAsDownloaded({ requestId }));
      } else {
        console.error('Failed to download PDF:', result.error);
      }
    }
  };

  const startQuoteGeneration = () => {
    if (!client?.intakeSurvey?.clientZipCode) {
      console.error('Client zipcode is not available');
      return;
    }

    const clientData = isTraditionalLTC
      ? {
          name: `${client.clientFirstName || ''} ${client.clientLastName || ''}`.trim(),
          age: quote.primaryAge,
          gender: quote.primaryGender,
        }
      : {
          name: `${client.clientFirstName || ''} ${client.clientLastName || ''}`.trim(),
          age: client.intakeSurvey?.clientAge || 0,
          gender: client.intakeSurvey?.clientGenderAtBirth,
        };

    const partnerData = !isTraditionalLTC && clientPartner
      ? {
          name: `${clientPartner.clientFirstName || ''} ${clientPartner.clientLastName || ''}`.trim(),
          age: clientPartner.intakeSurvey?.clientAge || 0,
          gender: clientPartner.intakeSurvey?.clientGenderAtBirth,
        }
      : undefined;

    dispatch(
      postIllustrationRequestRequest({
        insuranceQuoteId: quoteId,
        zipcode: client.intakeSurvey.clientZipCode,
        clientId: client.clientId,
        client: clientData,
        partner: partnerData,
        advisor: {
          advisorFirstName: advisor?.advisorFirstName,
          advisorLastName: advisor?.advisorLastName,
          organizationDisplayName: advisor?.organizationDisplayName,
          organizationName: advisor?.organizationName,
          advisorPhoneNumber: advisor?.advisorPhoneNumber,
        },
        premiumAmount: currentPremium,
        quoteType: isTraditionalLTC ? 'traditionalLTC' : 'insurance',
        fundingSourceId,
      }),
    );
  };

  // Simplified create illustration function for the download flow
  const handleCreateIllustration = () => {
    // If another quote is being created, don't start a new one
    if (isAnotherQuoteBeingCreated) {
      console.log('Another quote is already being created');
      return;
    }

    // Mark that user initiated a download so we can auto-download when PDF is ready
    userInitiatedDownload.current = true;
    hasAutoDownloaded.current = false;
    
    startQuoteGeneration();
  };

  // Auto-download PDF when it becomes available after user initiated download
  useEffect(() => {
    if (userInitiatedDownload.current && 
        hasIllustrationPdf && 
        !hasAutoDownloaded.current &&
        !isCreatingIllustration) {

      let pdfUrl: string | null = null;
      let requestId: string | null = null;

      if (hasValidCompletedIllustration && completedIllustration) {
        pdfUrl = completedIllustration.pdfUrl;
        requestId = completedIllustration.requestId;
      } else if (hasValidActiveIllustration && activeRequest) {
        pdfUrl = activeRequest.pdfUrl;
        requestId = activeRequest.requestId;
      }

      if (pdfUrl && requestId && !completedIllustration?.downloaded) {
        console.log('Auto-downloading PDF:', pdfUrl);
        hasAutoDownloaded.current = true;
        
        // Auto-download the PDF
        fetch(pdfUrl)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `policy-illustration-${quoteId}.pdf`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            console.log('PDF auto-download triggered');
          })
          .catch(error => {
            console.error('Failed to auto-download PDF:', error);
          });
          
        dispatch(markIllustrationAsDownloaded({ requestId }));
      }
    }
  }, [hasIllustrationPdf, isCreatingIllustration, hasValidCompletedIllustration, hasValidActiveIllustration, completedIllustration, activeRequest, quoteId, dispatch]);

  return {
    hasIllustrationPdf,
    isCreatingIllustration,
    isAnotherQuoteBeingCreated,
    handleViewPdf,
    handleDownloadPdf,
    handleCreateIllustration,
  };
}